<template>
  <div class="flex flex-col gap-2">
    <el-button
      class="w-full !mx-0"
      type="light"
      @click="uploadBackup"
      :loading="uploadLoading"
      :disabled="uploadLoading || downloadLoading"
    >
      <span v-if="!uploadLoading">Upload Backup</span>
      <span v-else>Importing Database...</span>
    </el-button>

    <el-button
      class="w-full !mx-0"
      type="light"
      @click="downloadBackup"
      :loading="downloadLoading"
      :disabled="uploadLoading || downloadLoading"
    >
      <span v-if="!downloadLoading">Download Backup</span>
      <span v-else>Creating Backup...</span>
    </el-button>

    <!-- Hidden file input for upload -->
    <input
      ref="fileInput"
      type="file"
      accept=".sql,.dump,.zip"
      style="display: none"
      @change="handleFileSelect"
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useHTTP } from '@/plugins/http'

// Composables
const http = useHTTP()

// Refs
const fileInput = ref<HTMLInputElement>()
const uploadLoading = ref(false)
const downloadLoading = ref(false)
const selectedFile = ref<File | null>(null)

// Upload backup function
const uploadBackup = async () => {
  // Trigger file input
  fileInput.value?.click()
}

// Handle file selection
const handleFileSelect = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]

  if (!file) return

  selectedFile.value = file

  // Show confirmation dialog
  try {
    await ElMessageBox.confirm(
      `Are you sure you want to import "${file.name}"? This will completely replace the current database and temporarily make the system unavailable.`,
      'Confirm Database Import',
      {
        confirmButtonText: 'Yes, Import',
        cancelButtonText: 'Cancel',
        type: 'warning',
        dangerouslyUseHTMLString: false
      }
    )

    // Proceed with upload
    await performUpload(file)
  } catch {
    // User cancelled
    selectedFile.value = null
    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

// Perform the actual upload
const performUpload = async (file: File) => {
  uploadLoading.value = true

  try {
    // Create FormData
    const formData = new FormData()
    formData.append('sql_file', file)

    // Make the request
    const response = await http.post('/system/database/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 300000 // 5 minutes timeout for large imports
    })

    ElMessage.success({
      message: response.message || 'Database imported successfully!',
      duration: 5000
    })

    // Optional: Reload the page after successful import
    setTimeout(() => {
      window.location.reload()
    }, 2000)
  } catch (error: any) {
    console.error('Upload failed:', error)

    let errorMessage = 'Failed to import database'
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }

    ElMessage.error({
      message: errorMessage,
      duration: 8000
    })
  } finally {
    uploadLoading.value = false
    selectedFile.value = null
    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = ''
    }
  }
}

// Download backup function
const downloadBackup = async () => {
  downloadLoading.value = true

  try {
    // Make the request to get the backup file
    const response = await http.get('/system/database/dump', {
      responseType: 'blob',
      timeout: 300000 // 5 minutes timeout for large backups
    })

    // Create blob URL and download
    const blob = new Blob([response], { type: 'application/sql' })
    const url = window.URL.createObjectURL(blob)

    // Create download link
    const link = document.createElement('a')
    link.href = url

    // Generate filename with current timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
    link.download = `database_backup_${timestamp}.sql`

    // Trigger download
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up blob URL
    window.URL.revokeObjectURL(url)

    ElMessage.success('Database backup downloaded successfully!')
  } catch (error: any) {
    console.error('Download failed:', error)

    let errorMessage = 'Failed to download database backup'
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message
    } else if (error.message) {
      errorMessage = error.message
    }

    ElMessage.error({
      message: errorMessage,
      duration: 8000
    })
  } finally {
    downloadLoading.value = false
  }
}
</script>

<style scoped>
/* Add any custom styles here if needed */
</style>
